import { Tooltip } from './popover/tooltip';

export const WEEKS = 52;
export const DAYS_PER_WEEK = 7;

const CELL_PX = 12;
const GAP_PX = 2;
const DAY_LABEL_GUTTER_PX = 20;
const DAY_LABEL_ROWS = [
  { key: 'mon', label: 'M' },
  { key: 'tue', label: '' },
  { key: 'wed', label: 'W' },
  { key: 'thu', label: '' },
  { key: 'fri', label: 'F' },
  { key: 'sat', label: '' },
  { key: 'sun', label: 'S' },
] as const;

export interface DayBucket {
  date: Date;
  dateKey: string;
  count: number;
  visible: boolean;
}

export interface MonthLabel {
  weekIndex: number;
  label: string;
}

export function colorLevel(count: number): string {
  if (count === 0) {
    return 'bg-gray-100';
  }

  if (count === 1) {
    return 'bg-primary-200';
  }

  if (count <= 3) {
    return 'bg-primary-300';
  }

  if (count <= 7) {
    return 'bg-primary-500';
  }

  return 'bg-primary-700';
}

export interface HeatmapStripProps {
  title: string;
  description: string;
  weeks: DayBucket[][];
  monthLabels: MonthLabel[];
  cellTooltip: (bucket: DayBucket) => string;
  onCellClick: (dateKey: string) => void;
}

// Presentation only — domain logic (bucketing, strip ordering, container
// layout) belongs in the caller.
export function HeatmapStrip({
  title,
  description,
  weeks,
  monthLabels,
  cellTooltip,
  onCellClick,
}: HeatmapStripProps) {
  const stride = CELL_PX + GAP_PX;

  return (
    <div>
      <h3 className={'text-base font-semibold text-gray-900 mb-3'}>{title}</h3>
      <p className="mt-0.5 mb-3 text-sm text-gray-600">{description}</p>

      {/* Month axis */}
      <div
        className="relative h-4 text-xs text-gray-500"
        style={{ marginLeft: DAY_LABEL_GUTTER_PX, width: WEEKS * stride }}
      >
        {monthLabels.map((m) => (
          <span key={m.weekIndex} className="absolute top-0" style={{ left: m.weekIndex * stride }}>
            {m.label}
          </span>
        ))}
      </div>

      {/* Body: weekday axis + grid */}
      <div className="flex gap-1">
        <div
          className="flex flex-col text-right text-xs text-gray-500"
          style={{ gap: GAP_PX, width: DAY_LABEL_GUTTER_PX - 4 }}
        >
          {DAY_LABEL_ROWS.map(({ key, label }) => (
            <div key={key} style={{ height: CELL_PX, lineHeight: `${CELL_PX}px` }}>
              {label}
            </div>
          ))}
        </div>

        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${WEEKS}, ${CELL_PX}px)`,
            gridTemplateRows: `repeat(${DAYS_PER_WEEK}, ${CELL_PX}px)`,
            gridAutoFlow: 'column',
            gap: GAP_PX,
          }}
        >
          {weeks.flatMap((week) =>
            week.map((bucket) => (
              <Cell
                key={bucket.dateKey}
                bucket={bucket}
                tooltip={cellTooltip(bucket)}
                onClick={onCellClick}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

interface CellProps {
  bucket: DayBucket;
  tooltip: string;
  onClick: (dateKey: string) => void;
}

function Cell({ bucket, tooltip, onClick }: CellProps) {
  if (!bucket.visible) {
    return <div data-heatmap-cell="" style={{ width: CELL_PX, height: CELL_PX }} />;
  }

  const clickable = bucket.count > 0;

  return (
    <Tooltip content={tooltip} side="bottom">
      <button
        type="button"
        data-heatmap-cell=""
        onClick={() => clickable && onClick(bucket.dateKey)}
        aria-label={tooltip}
        aria-disabled={!clickable}
        className={`rounded-sm ${colorLevel(bucket.count)} ${
          clickable ? 'cursor-pointer hover:ring-1 hover:ring-primary-300' : 'cursor-default'
        }`}
        style={{ width: CELL_PX, height: CELL_PX }}
      />
    </Tooltip>
  );
}

// Implementation note: the button is intentionally not `disabled` — a native
// `disabled` button suppresses pointer events, so the tooltip's hover listener
// wouldn't fire on empty cells (which still need a "no activity" tooltip). We
// use `aria-disabled` for accessibility and gate the click handler instead.
